import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, Trash2, Loader2, ExternalLink, Edit, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';
import { articlesService, type BlogArticle } from '../services/articles.service';
import { useAuth } from '@/shared/contexts/AuthContext';
import { formatDate } from '@/shared/lib/utils';

type ArticleStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'PUBLISHED';

const STATUS_CONFIG: Record<
  ArticleStatus,
  { label: string; variant: 'default' | 'success' | 'warning' | 'error' }
> = {
  PENDING: { label: 'Pendente', variant: 'warning' },     // Amarelo
  REJECTED: { label: 'Rejeitado', variant: 'error' },     // Vermelho
  APPROVED: { label: 'Aprovado', variant: 'default' },    // Azul
  PUBLISHED: { label: 'Publicado', variant: 'success' },  // Verde
};

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const [articleToApprove, setArticleToApprove] = useState<BlogArticle | null>(null);
  const [articleToReject, setArticleToReject] = useState<BlogArticle | null>(null);
  const [articleToPublish, setArticleToPublish] = useState<BlogArticle | null>(null);
  const [articleToDelete, setArticleToDelete] = useState<BlogArticle | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRegeneratingImage, setIsRegeneratingImage] = useState(false);

  useEffect(() => {
    if (id) {
      loadArticle();
    }
  }, [id]);

  const loadArticle = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await articlesService.getArticle(id);
      setArticle(data);
      setEditedTitle(data.title);
      setEditedContent(data.content);
    } catch (error) {
      toast.error('Erro ao carregar artigo', error instanceof Error ? error.message : undefined);
      navigate('/blog/articles');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmApprove = async () => {
    if (!user?.email || !articleToApprove) return;
    try {
      setIsProcessing(true);
      await articlesService.approveArticle(articleToApprove.id, user.email);
      toast.success('Artigo aprovado!');
      loadArticle();
      setArticleToApprove(null);
    } catch (error) {
      toast.error('Erro ao aprovar artigo', error instanceof Error ? error.message : undefined);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!user?.email || !articleToReject) return;
    try {
      setIsProcessing(true);
      await articlesService.rejectArticle(articleToReject.id, user.email);
      toast.success('Artigo rejeitado');
      loadArticle();
      setArticleToReject(null);
    } catch (error) {
      toast.error('Erro ao rejeitar artigo', error instanceof Error ? error.message : undefined);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmPublish = async () => {
    if (!articleToPublish) return;
    try {
      setIsProcessing(true);
      await articlesService.publishArticle(articleToPublish.id);
      toast.success('Artigo publicado!');
      loadArticle();
      setArticleToPublish(null);
    } catch (error) {
      toast.error('Erro ao publicar artigo', error instanceof Error ? error.message : undefined);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!articleToDelete) return;
    try {
      setIsProcessing(true);
      await articlesService.deleteArticle(articleToDelete.id);
      toast.success('Artigo deletado');
      navigate('/blog/articles');
    } catch (error) {
      toast.error('Erro ao deletar artigo', error instanceof Error ? error.message : undefined);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!article) return;
    try {
      await articlesService.updateArticle(article.id, {
        title: editedTitle,
        content: editedContent,
      });
      toast.success('Artigo atualizado!');
      loadArticle();
      setIsEditing(false);
    } catch (error) {
      toast.error('Erro ao atualizar artigo', error instanceof Error ? error.message : undefined);
    }
  };

  const handleRegenerateImage = async () => {
    if (!article) return;
    try {
      setIsRegeneratingImage(true);
      const updatedArticle = await articlesService.regenerateCoverImage(article.id);
      toast.success('Nova imagem gerada!');
      setArticle(updatedArticle);
    } catch (error) {
      toast.error('Erro ao regenerar imagem', error instanceof Error ? error.message : undefined);
    } finally {
      setIsRegeneratingImage(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 px-4 space-y-6">
      {/* Header com botão voltar */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/blog/articles')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{article.title}</h1>
          <Badge variant={STATUS_CONFIG[article.status].variant} className="mt-2">
            {STATUS_CONFIG[article.status].label}
          </Badge>
        </div>
      </div>

      {/* Conteúdo */}
      <Card>
        <CardContent className="space-y-6 pt-6">
          {/* Imagem de capa */}
          {article.coverImage && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <strong className="text-sm text-muted-foreground">Imagem de capa</strong>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRegenerateImage}
                  disabled={isRegeneratingImage}
                >
                  {isRegeneratingImage ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Gerar Nova Imagem
                    </>
                  )}
                </Button>
              </div>
              <div className="w-full rounded-lg overflow-hidden border">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          )}

          {/* Informações */}
          <div className="grid grid-cols-2 gap-4 text-sm bg-secondary/30 p-4 rounded-lg">
            <div>
              <strong className="text-muted-foreground">Gerado em:</strong>
              <p>{formatDate(article.generatedAt)}</p>
            </div>
            <div>
              <strong className="text-muted-foreground">Slug:</strong>
              <p className="font-mono text-xs">{article.slug}</p>
            </div>
            {article.category && (
              <div>
                <strong className="text-muted-foreground">Categoria:</strong>
                <p>{article.category}</p>
              </div>
            )}
            <div>
              <strong className="text-muted-foreground">Autor:</strong>
              <p>{article.author}</p>
            </div>
            {article.readingTime && (
              <div>
                <strong className="text-muted-foreground">Tempo de leitura:</strong>
                <p>{article.readingTime} min</p>
              </div>
            )}
            {article.reviewedBy && (
              <div>
                <strong className="text-muted-foreground">Revisado por:</strong>
                <p>{article.reviewedBy}</p>
              </div>
            )}
            {article.publishedAt && (
              <div>
                <strong className="text-muted-foreground">Publicado em:</strong>
                <p>{formatDate(article.publishedAt)}</p>
              </div>
            )}
          </div>

          {/* Keywords e Tags - Grid 50/50 */}
          {(article.keywords.length > 0 || (article.tags && article.tags.length > 0)) && (
            <div className="grid grid-cols-2 gap-6">
              {/* Keywords */}
              {article.keywords.length > 0 && (
                <div>
                  <strong className="text-sm text-muted-foreground">Palavras-chave:</strong>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {article.keywords.map((keyword, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div>
                  <strong className="text-sm text-muted-foreground">Tags:</strong>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {article.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-md text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Título editável */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium mb-2">Título</label>
              <Input
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="w-full"
              />
            </div>
          )}

          {/* Conteúdo */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <strong className="text-sm font-medium">Conteúdo</strong>
              <div className="flex gap-2">
                {isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                  >
                    {showPreview ? 'Editar' : 'Ver Prévia'}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsEditing(!isEditing);
                    setShowPreview(false);
                  }}
                >
                  {isEditing ? 'Cancelar Edição' : <><Edit className="h-4 w-4 mr-1" /> Editar</>}
                </Button>
              </div>
            </div>

            {isEditing ? (
              showPreview ? (
                <div className="bg-secondary/20 rounded-lg p-6 border min-h-[400px]">
                  {editedContent ? (
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <div dangerouslySetInnerHTML={{ __html: editedContent.replace(/\n/g, '<br />') }} />
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">Conteúdo vazio</p>
                  )}
                </div>
              ) : (
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  className="min-h-[500px] font-mono text-sm"
                  placeholder="Conteúdo do artigo em Markdown..."
                />
              )
            ) : (
              <div className="bg-secondary/20 rounded-lg p-6 border min-h-[400px]">
                {article.content ? (
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed m-0">
                    {article.content}
                  </pre>
                ) : (
                  <p className="text-muted-foreground text-sm">Sem conteúdo disponível</p>
                )}
              </div>
            )}
          </div>

          {/* Ações */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            {isEditing && (
              <Button onClick={handleSaveEdit}>
                <Check className="h-4 w-4 mr-2" />
                Salvar Alterações
              </Button>
            )}
            {!isEditing && (
              <>
                {article.status === 'PENDING' && (
                  <>
                    <Button variant="outline" onClick={() => setArticleToReject(article)}>
                      <X className="h-4 w-4 mr-2" />
                      Rejeitar
                    </Button>
                    <Button onClick={() => setArticleToApprove(article)}>
                      <Check className="h-4 w-4 mr-2" />
                      Aprovar
                    </Button>
                  </>
                )}
                {article.status === 'APPROVED' && (
                  <Button onClick={() => setArticleToPublish(article)}>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Publicar
                  </Button>
                )}
                {article.status !== 'PUBLISHED' && (
                  <Button variant="destructive" onClick={() => setArticleToDelete(article)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Deletar
                  </Button>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialogs de Confirmação */}
      <ConfirmDialog
        open={!!articleToApprove}
        onOpenChange={(open) => !open && setArticleToApprove(null)}
        title="Aprovar Artigo"
        description={`Tem certeza que deseja aprovar o artigo "${articleToApprove?.title}"? Após aprovado, o artigo poderá ser publicado.`}
        confirmText="Aprovar"
        cancelText="Cancelar"
        onConfirm={handleConfirmApprove}
        variant="default"
        icon="check"
        isLoading={isProcessing}
      />

      <ConfirmDialog
        open={!!articleToReject}
        onOpenChange={(open) => !open && setArticleToReject(null)}
        title="Rejeitar Artigo"
        description={`Tem certeza que deseja rejeitar o artigo "${articleToReject?.title}"? Esta ação pode ser revertida posteriormente.`}
        confirmText="Rejeitar"
        cancelText="Cancelar"
        onConfirm={handleConfirmReject}
        variant="warning"
        icon="warning"
        isLoading={isProcessing}
      />

      <ConfirmDialog
        open={!!articleToPublish}
        onOpenChange={(open) => !open && setArticleToPublish(null)}
        title="Publicar Artigo"
        description={`Tem certeza que deseja publicar o artigo "${articleToPublish?.title}"? O artigo ficará disponível publicamente após a publicação.`}
        confirmText="Publicar"
        cancelText="Cancelar"
        onConfirm={handleConfirmPublish}
        variant="default"
        icon="check"
        isLoading={isProcessing}
      />

      <ConfirmDialog
        open={!!articleToDelete}
        onOpenChange={(open) => !open && setArticleToDelete(null)}
        title="Deletar Artigo"
        description={`Tem certeza que deseja deletar o artigo "${articleToDelete?.title}"? Esta ação não pode ser desfeita.`}
        confirmText="Deletar"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        icon="delete"
        isLoading={isProcessing}
      />
    </div>
  );
}
